import React, { useState, useMemo, useEffect, useRef } from 'react';
import {
  Search, Download, Eye, X, RefreshCw, RotateCcw,
  TrendingUp, DollarSign,
  AlertTriangle, Clock, ArrowUpRight, ArrowDownRight,
} from 'lucide-react';

import BaseModal from '../../../components/BaseModal/BaseModal';
import StatusBadge, { StatusVariant } from '../../../components/StatusBadge/StatusBadge';
import Button from '../../../components/Button/Button';
import DataTable, { Column } from '../../../components/DataTable/DataTable';
import Pagination from '../../../components/Pagination/Pagination';

import styles from './Payments.module.css';

interface Payment {
  id: string;
  tenant: string;
  email: string;
  plan: string;
  amount: number;
  currency: string;
  gateway: 'Stripe' | 'Razorpay' | 'PayPal';
  status: 'Completed' | 'Pending' | 'Failed' | 'Refunded';
  date: string;
  method: string;
}

interface MonthlyRevenue {
  month: string;
  monthFull: string;
  revenue: number;
  payments: number;
  growth: number;
}

const PAYMENTS: Payment[] = [
  { id: 'TXN-10201', tenant: 'Al-Safar Travel', email: 'admin@alsafar.ae', plan: 'Advanced', amount: 199, currency: 'USD', gateway: 'Stripe', status: 'Completed', date: 'Mar 15, 2025', method: 'Visa •••• 4242' },
  { id: 'TXN-10202', tenant: 'JetStream Agency', email: 'ops@jetstream.pk', plan: 'Standard', amount: 149, currency: 'USD', gateway: 'Stripe', status: 'Completed', date: 'Mar 15, 2025', method: 'MC •••• 8831' },
  { id: 'TXN-10203', tenant: 'SkyRoutes Travel', email: 'hello@skyroutes.io', plan: 'Standard', amount: 1428, currency: 'USD', gateway: 'Stripe', status: 'Completed', date: 'Mar 14, 2025', method: 'Visa •••• 1556' },
  { id: 'TXN-10204', tenant: 'Nexvoyage Ltd', email: 'admin@nexvoyage.com', plan: 'Starter', amount: 99, currency: 'USD', gateway: 'PayPal', status: 'Failed', date: 'Mar 14, 2025', method: 'PayPal' },
  { id: 'TXN-10205', tenant: 'Horizon Tours', email: 'hello@horizontours.ae', plan: 'Advanced', amount: 199, currency: 'USD', gateway: 'Stripe', status: 'Completed', date: 'Mar 13, 2025', method: 'Amex •••• 0093' },
  { id: 'TXN-10206', tenant: 'Wanderlust Co.', email: 'admin@wanderlust.co', plan: 'Standard', amount: 149, currency: 'USD', gateway: 'Razorpay', status: 'Refunded', date: 'Mar 13, 2025', method: 'UPI' },
  { id: 'TXN-10207', tenant: 'SwiftWings Travel', email: 'ops@swiftwings.sa', plan: 'Advanced', amount: 1908, currency: 'USD', gateway: 'Stripe', status: 'Completed', date: 'Mar 12, 2025', method: 'Visa •••• 7721' },
  { id: 'TXN-10208', tenant: 'Travelwala.com', email: 'admin@travelwala.com', plan: 'Standard', amount: 149, currency: 'USD', gateway: 'Stripe', status: 'Completed', date: 'Mar 12, 2025', method: 'MC •••• 3344' },
  { id: 'TXN-10209', tenant: 'JetTrail Travels', email: 'ceo@jettrail.net', plan: 'Starter', amount: 99, currency: 'USD', gateway: 'Stripe', status: 'Failed', date: 'Mar 11, 2025', method: 'Visa •••• 9012' },
  { id: 'TXN-10210', tenant: 'BlueSky Adventures', email: 'hi@bluesky.ae', plan: 'Advanced', amount: 199, currency: 'USD', gateway: 'Stripe', status: 'Completed', date: 'Mar 11, 2025', method: 'Visa •••• 5567' },
  { id: 'TXN-10211', tenant: 'PeakTravel IN', email: 'admin@peaktravel.in', plan: 'Starter', amount: 0, currency: 'USD', gateway: 'Stripe', status: 'Pending', date: 'Mar 10, 2025', method: '—' },
  { id: 'TXN-10212', tenant: 'FarHorizon Ltd', email: 'ops@farhorizon.co.uk', plan: 'Standard', amount: 149, currency: 'USD', gateway: 'Stripe', status: 'Completed', date: 'Mar 10, 2025', method: 'Visa •••• 2200' },
  { id: 'TXN-10213', tenant: 'GoldenRoute Tours', email: 'admin@goldenroute.sa', plan: 'Standard', amount: 1428, currency: 'USD', gateway: 'Stripe', status: 'Completed', date: 'Mar 9, 2025', method: 'MC •••• 6677' },
  { id: 'TXN-10214', tenant: 'TripNest Agency', email: 'hello@tripnest.in', plan: 'Starter', amount: 0, currency: 'USD', gateway: 'Stripe', status: 'Pending', date: 'Mar 9, 2025', method: '—' },
  { id: 'TXN-10215', tenant: 'Bajij Links', email: 'info@bajijlinks.pk', plan: 'Starter', amount: 0, currency: 'USD', gateway: 'Stripe', status: 'Pending', date: 'Mar 8, 2025', method: '—' },
  { id: 'TXN-10216', tenant: 'Al-Safar Travel', email: 'admin@alsafar.ae', plan: 'Advanced', amount: 199, currency: 'USD', gateway: 'Stripe', status: 'Completed', date: 'Feb 15, 2025', method: 'Visa •••• 4242' },
  { id: 'TXN-10217', tenant: 'JetStream Agency', email: 'ops@jetstream.pk', plan: 'Standard', amount: 149, currency: 'USD', gateway: 'Stripe', status: 'Completed', date: 'Feb 15, 2025', method: 'MC •••• 8831' },
  { id: 'TXN-10218', tenant: 'Horizon Tours', email: 'hello@horizontours.ae', plan: 'Advanced', amount: 199, currency: 'USD', gateway: 'Stripe', status: 'Completed', date: 'Feb 13, 2025', method: 'Amex •••• 0093' },
  { id: 'TXN-10219', tenant: 'SwiftWings Travel', email: 'ops@swiftwings.sa', plan: 'Advanced', amount: 199, currency: 'USD', gateway: 'Stripe', status: 'Completed', date: 'Feb 12, 2025', method: 'Visa •••• 7721' },
  { id: 'TXN-10220', tenant: 'Travelwala.com', email: 'admin@travelwala.com', plan: 'Standard', amount: 149, currency: 'USD', gateway: 'Stripe', status: 'Completed', date: 'Feb 11, 2025', method: 'MC •••• 3344' },
];

const MONTHLY_REVENUE: MonthlyRevenue[] = [
  { month: 'Oct', monthFull: 'October', revenue: 6200, payments: 41, growth: 0 },
  { month: 'Nov', monthFull: 'November', revenue: 7400, payments: 49, growth: 19.4 },
  { month: 'Dec', monthFull: 'December', revenue: 8100, payments: 54, growth: 9.5 },
  { month: 'Jan', monthFull: 'January', revenue: 9300, payments: 61, growth: 14.8 },
  { month: 'Feb', monthFull: 'February', revenue: 10800, payments: 68, growth: 16.1 },
  { month: 'Mar', monthFull: 'March', revenue: 12400, payments: 78, growth: 14.8 },
];

function paymentStatusVariant(s: Payment['status']): StatusVariant {
  const map: Record<string, StatusVariant> = { Completed: 'active', Pending: 'trial', Failed: 'error', Refunded: 'warning' };
  return map[s] || 'info';
}

const AnimatedCounter: React.FC<{ value: number; prefix?: string; duration?: number }> = ({ value, prefix = '', duration = 1200 }) => {
  const [display, setDisplay] = useState(0);
  const hasRun = useRef(false);
  useEffect(() => {
    if (hasRun.current) return;
    hasRun.current = true;
    const start = performance.now();
    const tick = (now: number) => {
      const p = Math.min((now - start) / duration, 1);
      setDisplay(Math.round((1 - Math.pow(1 - p, 3)) * value));
      if (p < 1) requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  }, [value, duration]);
  return <>{prefix}{display.toLocaleString()}</>;
};

const RevenueChart: React.FC<{ data: MonthlyRevenue[] }> = ({ data }) => {
  const [hovered, setHovered] = useState<number | null>(null);
  const [animate, setAnimate] = useState(false);
  const max = Math.max(...data.map(d => d.revenue));
  const total = data.reduce((s, d) => s + d.revenue, 0);

  useEffect(() => { const t = setTimeout(() => setAnimate(true), 100); return () => clearTimeout(t); }, []);

  return (
    <div className={styles.revenueChartWrap}>
      <div className={styles.revenueChartHeader}>
        <div>
          <div className={styles.revenueChartLabel}>Total Revenue</div>
          <div className={styles.revenueChartTotal}><AnimatedCounter value={total} prefix="$" /></div>
          <div className={styles.revenueChartPeriod}>Last 6 months</div>
        </div>
        <div className={styles.revenueChartBadge}><ArrowUpRight size={12} /><span>+14.8%</span></div>
      </div>
      <div className={styles.revenueGridLines}>
        {[0, 25, 50, 75, 100].map(pct => (
          <div key={pct} className={styles.revenueGridLine} style={{ bottom: `${pct}%` }}>
            {pct > 0 && <span className={styles.revenueGridLabel}>${Math.round((max * pct) / 100 / 1000)}k</span>}
          </div>
        ))}
      </div>
      <div className={styles.revenueChart}>
        {data.map((d, i) => {
          const h = Math.max((d.revenue / max) * 100, 6);
          const isLast = i === data.length - 1;
          const isH = hovered === i;
          return (
            <div key={d.month} className={styles.revenueBarCol} onMouseEnter={() => setHovered(i)} onMouseLeave={() => setHovered(null)}>
              {isH && (
                <div className={styles.revenueTooltip}>
                  <div className={styles.tooltipMonth}>{d.monthFull}</div>
                  <div className={styles.tooltipRevenue}>${d.revenue.toLocaleString()}</div>
                  <div className={styles.tooltipMeta}>
                    <span>{d.payments} payments</span>
                    {d.growth > 0 && <span className={styles.tooltipGrowth}>+{d.growth}%</span>}
                  </div>
                </div>
              )}
              <div className={styles.revenueBarTrack}>
                <div className={`${styles.revenueBarFill} ${isLast ? styles.revenueBarCurrent : ''} ${isH ? styles.revenueBarHovered : ''}`}
                  style={{ height: animate ? `${h}%` : '0%', transitionDelay: `${i * 80}ms` }}>
                  {isLast && <div className={styles.revenueBarShine} />}
                </div>
              </div>
              <div className={styles.revenueBarBottom}>
                <span className={`${styles.revenueBarLabel} ${isLast || isH ? styles.revenueBarLabelActive : ''}`}>{d.month}</span>
                <span className={`${styles.revenueBarAmount} ${isLast || isH ? styles.revenueBarAmountVisible : ''}`}>${(d.revenue / 1000).toFixed(1)}k</span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const GatewayDonut: React.FC = () => {
  const [animate, setAnimate] = useState(false);
  const gateways = [
    { name: 'Stripe', pct: 78, color: '#1B2A6B', amount: '$42,800' },
    { name: 'Razorpay', pct: 14, color: '#F97316', amount: '$7,680' },
    { name: 'PayPal', pct: 8, color: '#c1c7d8', amount: '$4,390' },
  ];
  useEffect(() => { const t = setTimeout(() => setAnimate(true), 300); return () => clearTimeout(t); }, []);
  const r = 52, circ = 2 * Math.PI * r;
  let cum = 0;
  return (
    <div className={styles.gatewaySection}>
      <div className={styles.gatewayDonutWrap}>
        <svg viewBox="0 0 128 128" className={styles.gatewayDonutSvg}>
          {gateways.map((g, i) => {
            const dash = (g.pct / 100) * circ;
            const gap = circ - dash;
            const off = -cum;
            cum += dash;
            return <circle key={g.name} cx="64" cy="64" r={r} fill="none" stroke={g.color} strokeWidth="14"
              strokeDasharray={`${dash} ${gap}`} strokeDashoffset={animate ? off : circ}
              strokeLinecap="round" className={styles.gatewayDonutArc} style={{ transitionDelay: `${i * 150}ms` }} />;
          })}
        </svg>
        <div className={styles.gatewayDonutCenter}>
          <span className={styles.gatewayDonutTotal}><AnimatedCounter value={54870} prefix="$" /></span>
          <span className={styles.gatewayDonutLabel}>Total</span>
        </div>
      </div>
      <div className={styles.gatewayLegend}>
        {gateways.map(g => (
          <div key={g.name} className={styles.gatewayLegendItem}>
            <span className={styles.gatewayLegendDot} style={{ background: g.color }} />
            <span className={styles.gatewayLegendName}>{g.name}</span>
            <span className={styles.gatewayLegendAmount}>{g.amount}</span>
            <span className={styles.gatewayLegendPct}>{g.pct}%</span>
          </div>
        ))}
      </div>
    </div>
  );
};

const StatusBreakdown: React.FC = () => {
  const [animate, setAnimate] = useState(false);
  const statuses = [
    { label: 'Completed', count: PAYMENTS.filter(p => p.status === 'Completed').length, total: PAYMENTS.length, color: '#16A34A' },
    { label: 'Pending', count: PAYMENTS.filter(p => p.status === 'Pending').length, total: PAYMENTS.length, color: '#F59E0B' },
    { label: 'Failed', count: PAYMENTS.filter(p => p.status === 'Failed').length, total: PAYMENTS.length, color: '#DC2626' },
    { label: 'Refunded', count: PAYMENTS.filter(p => p.status === 'Refunded').length, total: PAYMENTS.length, color: '#8a93b0' },
  ];
  useEffect(() => { const t = setTimeout(() => setAnimate(true), 500); return () => clearTimeout(t); }, []);
  return (
    <div className={styles.statusBreakdown}>
      {statuses.map((s, i) => (
        <div key={s.label} className={styles.statusRow}>
          <span className={styles.statusDot} style={{ background: s.color }} />
          <span className={styles.statusName}>{s.label}</span>
          <div className={styles.statusBarTrack}>
            <div className={styles.statusBarFill} style={{ width: animate ? `${Math.round((s.count / s.total) * 100)}%` : '0%', background: s.color, transitionDelay: `${i * 100}ms` }} />
          </div>
          <span className={styles.statusCount}>{s.count}</span>
        </div>
      ))}
    </div>
  );
};

const PaymentDetailDrawer: React.FC<{ payment: Payment | null; onClose: () => void; onRefund: (p: Payment) => void }> = ({ payment, onClose, onRefund }) => {
  if (!payment) return null;
  return (
    <>
      <div className={styles.drawerBackdrop} onClick={onClose} />
      <aside className={styles.drawer} aria-label="Payment details">
        <div className={styles.drawerHeader}>
          <div><h3 className={styles.drawerName}>{payment.id}</h3><span className={styles.drawerId}>{payment.tenant}</span></div>
          <button className={styles.drawerClose} onClick={onClose} aria-label="Close"><X size={14} strokeWidth={2.5} /></button>
        </div>
        <div className={styles.drawerBody}>
          <div className={styles.drawerAmountBlock}>
            <span className={styles.drawerAmountLabel}>Amount</span>
            <span className={styles.drawerAmount}>${payment.amount.toLocaleString()}</span>
            <span className={styles.drawerAmountCurrency}>{payment.currency}</span>
          </div>
          <div className={styles.drawerStats}>
            <div className={styles.drawerStatItem}><span className={styles.drawerStatLabel}>Status</span><StatusBadge variant={paymentStatusVariant(payment.status)} size="sm" /></div>
            <div className={styles.drawerStatItem}><span className={styles.drawerStatLabel}>Gateway</span><span className={styles.drawerStatValue}>{payment.gateway}</span></div>
            <div className={styles.drawerStatItem}><span className={styles.drawerStatLabel}>Date</span><span className={styles.drawerStatValue}>{payment.date}</span></div>
          </div>
          <section className={styles.drawerSection}>
            <h4 className={styles.drawerSectionTitle}>Transaction Details</h4>
            {[{ label: 'Transaction ID', value: payment.id },{ label: 'Tenant', value: payment.tenant },{ label: 'Email', value: payment.email },{ label: 'Plan', value: payment.plan },{ label: 'Payment Method', value: payment.method },{ label: 'Gateway', value: payment.gateway },{ label: 'Currency', value: payment.currency },{ label: 'Date', value: payment.date }].map(row => (
              <div key={row.label} className={styles.drawerRow}><span className={styles.drawerRowLabel}>{row.label}</span><span className={styles.drawerRowValue}>{row.value}</span></div>
            ))}
          </section>
          {payment.status === 'Completed' && (
            <section className={styles.drawerSection}><h4 className={styles.drawerSectionTitle}>Actions</h4>
              <div className={styles.drawerActions}><Button variant="secondary" size="sm" icon={<RotateCcw size={14} />} onClick={() => onRefund(payment)}>Issue Refund</Button><Button variant="ghost" size="sm" icon={<Download size={14} />}>Download Receipt</Button></div>
            </section>
          )}
          {payment.status === 'Failed' && (
            <section className={styles.drawerSection}><h4 className={styles.drawerSectionTitle}>Actions</h4>
              <div className={styles.drawerActions}><Button variant="secondary" size="sm" icon={<RefreshCw size={14} />}>Retry Payment</Button></div>
            </section>
          )}
        </div>
      </aside>
    </>
  );
};

const RefundModal: React.FC<{ payment: Payment | null; onClose: () => void; onConfirm: () => void }> =
  ({ payment, onClose, onConfirm }) => (
    <BaseModal isOpen={!!payment} onClose={onClose} title="Issue Refund" subtitle={payment?.id} size="sm"
      footer={<><Button variant="secondary" size="sm" onClick={onClose}>Cancel</Button><Button variant="danger" size="sm" icon={<RotateCcw size={14} />} onClick={onConfirm}>Confirm Refund</Button></>}>
      {payment && (
        <div className={styles.confirmBody}>
          <div className={styles.confirmIcon}><RotateCcw size={24} /></div>
          <p className={styles.confirmText}>Refund <strong>${payment.amount.toLocaleString()} {payment.currency}</strong> to <strong>{payment.tenant}</strong>? This will reverse the payment via {payment.gateway} and update the subscription status.</p>
          <div className={styles.confirmMeta}><span>{payment.id}</span><span>{payment.method}</span><span>{payment.date}</span></div>
        </div>
      )}
    </BaseModal>
  );

const PaymentsPage: React.FC = () => {
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const [gatewayFilter, setGatewayFilter] = useState('All');
  const [page, setPage] = useState(1);
  const [detailPayment, setDetailPayment] = useState<Payment | null>(null);
  const [refundTarget, setRefundTarget] = useState<Payment | null>(null);
  const PER_PAGE = 10;

  const stats = useMemo(() => {
    const completed = PAYMENTS.filter(p => p.status === 'Completed');
    const totalCollected = completed.reduce((s, p) => s + p.amount, 0);
    const failed = PAYMENTS.filter(p => p.status === 'Failed').length;
    const refunded = PAYMENTS.filter(p => p.status === 'Refunded').reduce((s, p) => s + p.amount, 0);
    const pending = PAYMENTS.filter(p => p.status === 'Pending').length;
    const thisMonth = completed.filter(p => p.date.includes('Mar')).reduce((s, p) => s + p.amount, 0);
    return { totalCollected, failed, refunded, pending, thisMonth };
  }, []);

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return PAYMENTS.filter(p =>
      (!q || p.tenant.toLowerCase().includes(q) || p.email.toLowerCase().includes(q) || p.id.toLowerCase().includes(q)) &&
      (statusFilter === 'All' || p.status === statusFilter) &&
      (gatewayFilter === 'All' || p.gateway === gatewayFilter)
    );
  }, [search, statusFilter, gatewayFilter]);

  const paged = filtered.slice((page - 1) * PER_PAGE, page * PER_PAGE);
  const totalPages = Math.ceil(filtered.length / PER_PAGE);
  const clearFilters = () => { setSearch(''); setStatusFilter('All'); setGatewayFilter('All'); setPage(1); };
  const hasFilters = search || statusFilter !== 'All' || gatewayFilter !== 'All';

  const columns: Column<Payment>[] = [
    { key: 'txn', header: 'TRANSACTION', render: p => (<div className={styles.txnCell}><div className={styles.txnAvatar}>{p.tenant.slice(0, 2).toUpperCase()}</div><div><div className={styles.txnId}>{p.id}</div><div className={styles.txnTenant}>{p.tenant}</div></div></div>) },
    { key: 'plan', header: 'PLAN', render: p => { const v = p.plan.toLowerCase() as any; return <StatusBadge variant={v === 'standard' ? 'pro' : v === 'advanced' ? 'enterprise' : 'starter'} size="sm" dot={false} />; } },
    { key: 'amount', header: 'AMOUNT', render: p => (<span className={`${styles.numericCell} ${p.amount === 0 ? styles.numericMuted : ''}`}>{p.amount > 0 ? `$${p.amount.toLocaleString()}` : '—'}</span>) },
    { key: 'gateway', header: 'GATEWAY', render: p => <span className={styles.gatewayChip}>{p.gateway}</span> },
    { key: 'method', header: 'METHOD', render: p => <span className={styles.methodText}>{p.method}</span> },
    { key: 'status', header: 'STATUS', render: p => <StatusBadge variant={paymentStatusVariant(p.status)} size="sm" /> },
    { key: 'date', header: 'DATE', render: p => <span className={styles.dateText}>{p.date}</span> },
    { key: 'actions', header: '', render: p => (<div className={`${styles.actionBtns} actionBtns`} onClick={e => e.stopPropagation()}><button className={styles.actionBtn} title="View" onClick={() => setDetailPayment(p)}><Eye size={13} /></button>{p.status === 'Completed' && <button className={`${styles.actionBtn} ${styles.actionDanger}`} title="Refund" onClick={() => setRefundTarget(p)}><RotateCcw size={13} /></button>}{p.status === 'Failed' && <button className={styles.actionBtn} title="Retry"><RefreshCw size={13} /></button>}</div>) },
  ];

  return (
    <div className={styles.page}>
      <div className={styles.pageHeader}><div><h1 className={styles.pageTitle}>Payments & Revenue</h1><p className={styles.pageSubtitle}>Track all platform payments and monitor revenue</p></div><div className={styles.headerActions}><Button variant="secondary" size="md" icon={<Download size={14} />}>Export</Button></div></div>

      <div className={styles.statsGrid}>
        <div className={`${styles.statCard} ${styles.statCardAccent}`}><div className={styles.statTop}><span className={styles.statLabel}>Monthly Revenue</span><span className={`${styles.statIcon} ${styles.statIconAccent}`}><TrendingUp size={15} /></span></div><div className={`${styles.statValue} ${styles.statValueAccent}`}><AnimatedCounter value={stats.thisMonth} prefix="$" /></div><div className={styles.statSub}><span className={styles.statTrendUp}><ArrowUpRight size={11} /> 18%</span> vs last month</div></div>
        <div className={styles.statCard}><div className={styles.statTop}><span className={styles.statLabel}>Total Collected</span><span className={styles.statIcon}><DollarSign size={15} /></span></div><div className={styles.statValue}><AnimatedCounter value={stats.totalCollected} prefix="$" /></div><div className={styles.statSub}>All time</div></div>
        <div className={styles.statCard}><div className={styles.statTop}><span className={styles.statLabel}>Failed Payments</span><span className={styles.statIcon}><AlertTriangle size={15} /></span></div><div className={styles.statValue}>{stats.failed}</div><div className={styles.statSub}><span className={styles.statTrendDown}><ArrowDownRight size={11} /> 2</span> this month</div></div>
        <div className={styles.statCard}><div className={styles.statTop}><span className={styles.statLabel}>Refunds</span><span className={styles.statIcon}><RotateCcw size={15} /></span></div><div className={styles.statValue}><AnimatedCounter value={stats.refunded} prefix="$" /></div><div className={styles.statSub}>1 refund processed</div></div>
        <div className={styles.statCard}><div className={styles.statTop}><span className={styles.statLabel}>Pending</span><span className={styles.statIcon}><Clock size={15} /></span></div><div className={styles.statValue}>{stats.pending}</div><div className={styles.statSub}>Awaiting confirmation</div></div>
      </div>

      <div className={styles.chartsRow}>
        <div className={styles.chartCard}><RevenueChart data={MONTHLY_REVENUE} /></div>
        <div className={styles.chartCard}>
          <div className={styles.chartHeader}><div><h3 className={styles.chartTitle}>Gateway Split</h3><p className={styles.chartSub}>By payment volume</p></div></div>
          <GatewayDonut />
          <div className={styles.chartDivider} />
          <div className={styles.chartHeader}><div><h3 className={styles.chartTitle}>Payment Status</h3><p className={styles.chartSub}>Current month breakdown</p></div></div>
          <StatusBreakdown />
        </div>
      </div>

      <div className={styles.sectionHeader}><h2 className={styles.sectionTitle}>All Transactions</h2><p className={styles.sectionSub}>{PAYMENTS.length} total payments</p></div>

      <div className={styles.tableCard}>
        <div className={styles.toolbar}>
          <div className={styles.searchWrap}><Search size={14} className={styles.searchIcon} /><input className={styles.searchInput} value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} placeholder="Search by tenant, email, or ID…" />{search && <button className={styles.searchClear} onClick={() => setSearch('')}><X size={12} /></button>}</div>
          <div className={styles.filterPills}>{(['All', 'Completed', 'Pending', 'Failed', 'Refunded'] as const).map(s => (<button key={s} className={`${styles.pill} ${statusFilter === s ? styles.pillActive : ''}`} onClick={() => { setStatusFilter(s); setPage(1); }}>{s}</button>))}</div>
          <div className={styles.selects}><select className={styles.select} value={gatewayFilter} onChange={e => { setGatewayFilter(e.target.value); setPage(1); }}><option value="All">All Gateways</option><option value="Stripe">Stripe</option><option value="Razorpay">Razorpay</option><option value="PayPal">PayPal</option></select></div>
        </div>
        <div className={styles.resultsBar}><span className={styles.resultsText}>Showing <strong>{paged.length}</strong> of <strong>{filtered.length}</strong> transactions</span>{hasFilters && <button className={styles.clearFilters} onClick={clearFilters}><X size={10} /> Clear filters</button>}</div>
        <DataTable columns={columns} data={paged} keyExtractor={p => p.id} onRowClick={setDetailPayment} emptyState={<div className={styles.emptyState}><p className={styles.emptyTitle}>No transactions found</p><p className={styles.emptySub}>Try adjusting your search or filters</p>{hasFilters && <Button variant="ghost" size="sm" onClick={clearFilters}>Clear all filters</Button>}</div>} />
        <Pagination page={page} totalPages={totalPages} totalItems={filtered.length} perPage={PER_PAGE} onPageChange={p => setPage(p)} />
      </div>

      <PaymentDetailDrawer payment={detailPayment} onClose={() => setDetailPayment(null)} onRefund={p => { setDetailPayment(null); setRefundTarget(p); }} />
      <RefundModal payment={refundTarget} onClose={() => setRefundTarget(null)} onConfirm={() => setRefundTarget(null)} />
    </div>
  );
};

export default PaymentsPage;